import type { RouteLocationNormalized, RouteLocationRaw, Router } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import { routeNames } from "@/router/constants";
import { featureEnabled, onlyFeatureEnabled, onlyFeaturesEnabled } from "@/config";
import { useApolloClient } from "@vue/apollo-composable";
import { customerQuery } from "@/api/userApi";
import { useFragment } from "@/api/generated";
import { CustomerFragmentFragmentDoc, CustomerPreferencesFragmentFragmentDoc, CustomerStatus, Landing_Page } from "@/api/generated/graphql";
import { nextTick, ref } from "vue";
import { MoneyFeatures } from "@/config/constants";
import { getInternetBankingRoutes } from "@/router/routesBanking";
import { authRouteGuard, businessBankingContextRouteGuard, customerRouteGuard, type NavigationGuardNext, roleRouteGuard } from "@/router/utils";
import { getLendingRoutes } from "@/router/routesLending";
import { getOnboardingRoutes } from "@/router/routesOnboarding";
import { getBusinessOnboardingRoutes } from "@/router/routesBusinessOnboarding";
import { useIntl } from "@moroku/money-webapp-components";
import { getBaseRoutes } from "@/router/routesBase";
import * as Sentry from "@sentry/vue";
import useAuthUser from "@/composables/useAuthUser";

const { t } = useIntl();

let router: Router | undefined;

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    requiresInternalUser?: boolean;
    requiresBroker?: boolean;
    requiresAuth?: boolean; // default = true
    requiresBusinessBankingContext?: boolean;
    headerBottomMargin?: boolean; // whether the header includes a bottom margin, default = true
    alias?: string;
  }
}

const isLoadingAuthAndCustomer = ref(false);
export function useMoneyRouter() {
  return {
    isLoadingAuthAndCustomer,
  };
}

// Using the router outside Vue components
export const getRouter = () => {
  if (!router) {
    const error = new Error("Router has not been created yet");
    Sentry.captureException(error);
    throw error;
  }
  return router;
};

export const createMoneyRouter = () => {
  router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else {
        return { top: 0 };
      }
    },
    routes: [
      ...getBaseRoutes(),
      ...(featureEnabled(MoneyFeatures.InternetBanking) ? getInternetBankingRoutes() : []),
      ...(featureEnabled(MoneyFeatures.Lending) ? getLendingRoutes() : []),
      ...(featureEnabled(MoneyFeatures.Onboarding) ? getOnboardingRoutes() : []),
      ...(featureEnabled(MoneyFeatures.BusinessOnboarding) ? getBusinessOnboardingRoutes() : []),
      {
        path: "/page-not-found",
        alias: "/:catchAll(.*)?",
        name: routeNames.PAGE_NOT_FOUND,
        component: () => import("@/features/core/PageNotFoundView.vue"),
        meta: { requiresAuth: false },
      },
    ],
  });

  router.beforeResolve(beforeResolveTasks);

  router.afterEach(async to => {
    // Use `nextTick` to handle router history correctly
    // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
    await nextTick(() => {
      let baseTitle = t("tenantName");
      if (onlyFeatureEnabled(MoneyFeatures.InternetBanking)) {
        baseTitle = t("webApp_title_banking_base");
      } else if (onlyFeatureEnabled(MoneyFeatures.Lending)) {
        baseTitle = t("webApp_title_lending_base");
      } else if (onlyFeaturesEnabled(MoneyFeatures.Onboarding, MoneyFeatures.BusinessOnboarding)) {
        baseTitle = t("webApp_title_onboarding_base");
      }

      document.title = to.meta.title
        ? t("webApp_title_forView", {
            base: baseTitle,
            title: to.meta.title,
          })
        : baseTitle;
    });
  });

  return router;
};

async function beforeResolveTasks(to: RouteLocationNormalized): Promise<NavigationGuardNext> {
  isLoadingAuthAndCustomer.value = true;

  try {
    const authNavigation = await authRouteGuard(to);
    if (authNavigation?.goto != undefined) {
      return authNavigation?.goto;
    }

    if (authNavigation?.authUser) {
      const customerNavigation = await customerRouteGuard(to);
      if (customerNavigation != undefined) {
        return customerNavigation;
      }

      const roleNavigation = await roleRouteGuard(to);
      if (roleNavigation != undefined) {
        return roleNavigation;
      }

      const businessContextNavigation = businessBankingContextRouteGuard(to);
      if (businessContextNavigation != undefined) {
        return businessContextNavigation;
      }

      return redirectUser(to);
    }
  } finally {
    isLoadingAuthAndCustomer.value = false;
  }
}

async function redirectUser(to: RouteLocationNormalized): Promise<NavigationGuardNext> {
  if (
    [
      routeNames.HOME,
      routeNames.SIGN_IN_VIEW,
      routeNames.ONBOARDING_SPLASH,
      routeNames.LOAN_APPLICATION_SPLASH,
      routeNames.CUSTOMER_ERROR_VIEW,
    ].includes(to.name as any)
  ) {
    console.log(`Redirecting away from ${to.name?.toString()} to landing page`, to.query);
    return getCustomerLandingPage();
  }
}

export async function getCustomerLandingPage(): Promise<RouteLocationRaw | undefined> {
  const from = router?.currentRoute.value;

  try {
    const { isBroker } = useAuthUser();

    const { resolveClient } = useApolloClient();
    const client = resolveClient();

    const customerResult = await client.query({ query: customerQuery, fetchPolicy: "cache-first" });
    const customer = useFragment(CustomerFragmentFragmentDoc, customerResult.data.customer);
    const preferences = useFragment(CustomerPreferencesFragmentFragmentDoc, customer?.preferences);

    let to: RouteLocationRaw;

    if (isBroker.value) {
      to = { name: routeNames.BROKER_LOAN_APPLICATIONS };
    } else if (featureEnabled(MoneyFeatures.InternetBanking)) {
      to = { name: routeNames.FINANCIAL_OVERVIEW };
    } else if (featureEnabled(MoneyFeatures.Lending)) {
      to = { name: routeNames.LOAN_APPLICATIONS };
    } else if (featureEnabled(MoneyFeatures.Onboarding)) {
      to = { name: routeNames.ONBOARDING_VIEW };
    } else if (customer?.status === CustomerStatus.Active) {
      switch (preferences?.landingPage) {
        case Landing_Page.AccountDetails:
          if (!preferences.primaryAccountId) {
            to = { name: routeNames.FINANCIAL_OVERVIEW };
            break;
          }
          to = { name: routeNames.ACCOUNT_DETAILS_VIEW, params: { accountId: preferences.primaryAccountId } };
          break;
        case Landing_Page.Overview:
          to = { name: routeNames.FINANCIAL_OVERVIEW };
          break;
        default:
          to = { name: routeNames.HOME };
          break;
      }
    } else {
      to = { name: routeNames.REQUIRES_ONBOARDING_VIEW };
    }

    console.log(`Redirecting to customer landing page`, to);
    return to;
  } catch (e) {
    Sentry.captureException(e, {
      data: {
        message: "Could not determine user landing page, defaulting to an error page.",
      },
    });

    if (from?.name === routeNames.CUSTOMER_ERROR_VIEW) {
      console.error("Already on error view, not redirecting.");
      return;
    }

    return { name: routeNames.CUSTOMER_ERROR_VIEW };
  }
}
